import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient, QueryClient } from '@tanstack/react-query';
 
import axios from 'axios';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Select,
  Tag,
  Card,
  Popconfirm,
  message,
  ConfigProvider,
  Row,
  Col,
  Upload,
  Image,
  Divider,
  Space,
  Typography,
  Collapse
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  LoadingOutlined,
  UploadOutlined,
  EyeOutlined,
  DeleteTwoTone,
  InfoCircleOutlined
} from '@ant-design/icons';
import { API_BASE_URL } from '../components/l1';
const { TextArea } = Input;
const { Option } = Select;
 
// ====================
 

// Categories API (for dropdown)
const categoryApi = {
  getAll: () => axios.get(`${API_BASE_URL}/categories`).then(res => res.data),
};

// Products API
const productApi = {
  getAll: () => axios.get(`${API_BASE_URL}/products`).then(res => res.data),
  create: (data) => axios.post(`${API_BASE_URL}/products`, data).then(res => res.data),
  update: (id, data) => axios.put(`${API_BASE_URL}/products/${id}`, data).then(res => res.data),
  delete: (id) => axios.delete(`${API_BASE_URL}/products/${id}`).then(res => res.data),
};

 

 
const useCategories = () => {
  return useQuery({
    queryKey: ['categories'],
    queryFn: () => categoryApi.getAll(),
    select: (data) => data.data || [],
  });
};

// Products hooks
const useProducts = () => {
  return useQuery({
    queryKey: ['products'],
    queryFn: () => productApi.getAll(),
    select: (data) => data.data || [],
  });
};

const useCreateProduct = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data) => productApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      message.success('Product created successfully');
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Failed to create product');
    },
  });
};

const useUpdateProduct = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }) => productApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      message.success('Product updated successfully');
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Failed to update product');
    },
  });
};

const useDeleteProduct = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id) => productApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      message.success('Product deleted successfully');
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Failed to delete product');
    },
  });
};

 



const BriefPriceArray = ({ value = [], onChange }) => {
    // if(value && Array.isArray(value) &&value.length===0 )return 
    // console.log(value)
  const [items, setItems] = useState(() => {

    
    try {
      if (value && Array.isArray(value)) {
        return value.map(item => {
          const entries = Object.entries(item);
          if (entries.length > 0) {
            const [key, val] = entries[0];
            return { key, value: val };
          }
          return { key: '', value: '' };
        });
      }
    } catch (e) {
      console.error('Error parsing value:', e);
    }
    return [{ key: '', value: '' }];
  });


 






  // Update parent only when needed
  const updateParent = (newItems) => {
    const filtered = newItems.filter(item => 
      item.key && item.key.trim() && item.value !== '' && item.value !== null
    );
    
    const formatted = filtered.map(item => ({
      [item.key.trim()]: Number(item.value)
    }));
    
    onChange(formatted);
  };

  const handleChange = (index, field, newValue) => {
    const newItems = [...items];
    
    if (field === 'value') {
      // Handle empty value or number
      newItems[index] = {
        ...newItems[index],
        value: newValue === null || newValue === '' ? '' : Number(newValue)
      };
    } else {
      newItems[index] = {
        ...newItems[index],
        key: newValue
      };
    }
    
    setItems(newItems);
    updateParent(newItems);
  };

  const addItem = () => {
    const newItems = [...items, { key: '', value: '' }];
    setItems(newItems);
  };

  const removeItem = (index) => {
    if (items.length === 1) {
      // Clear the only item
      setItems([{ key: '', value: '' }]);
      updateParent([{ key: '', value: '' }]);
    } else {
      const newItems = items.filter((_, i) => i !== index);
      setItems(newItems);
      updateParent(newItems);
    }
  };

  return (
    <div className="space-y-3">
      {    items    .map((item, index) => (
        <div key={index} className="flex items-center gap-2">
          <Input
            placeholder="Option name"
            value={item.key}
            onChange={(e) => handleChange(index, 'key', e.target.value)}
            className="flex-1"
          />
          <InputNumber
            placeholder="Price"
            value={item.value}
            onChange={(val) => handleChange(index, 'value', val)}
            min={0}
            className="w-32"
          />
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => removeItem(index)}
          />
        </div>
      ))}
      <Button type="dashed" onClick={addItem} block>
        Add Price Option
      </Button>
    </div>
  );
};







// ====================
// 5. PRODUCT FORM COMPONENT
// ====================
const ProductForm = ({ open, onClose, editingProduct, categories = [] }) => {
  const [form] = Form.useForm();
  const createMutation = useCreateProduct();
  const updateMutation = useUpdateProduct();
  const [loading, setLoading] = useState(false);
  const [mainImage, setMainImage] = useState('');
  const [additionalImages, setAdditionalImages] = useState([]);

  // Set form values when editing
  useEffect(() => {
    if (editingProduct && open) {
      form.setFieldsValue({
        ...editingProduct,
        category: editingProduct.category || '',
        brifPrize: editingProduct.brifPrize || [],
        tags: editingProduct.tags ? editingProduct.tags.join(',') : ''
      });
      setMainImage(editingProduct.image || '');
      setAdditionalImages(editingProduct.images || []);
    } else {
      form.resetFields();
      setMainImage('');
      setAdditionalImages([]);
    }
  }, [editingProduct, open, form]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      // Process tags from string to array
      const processedValues = {
        ...values,
        tags: values.tags ? values.tags.split(',').map(tag => tag.trim()) : [],
        images: additionalImages,
        image: mainImage,
        // Ensure category is stored as string name
        category: values.category,
        // Ensure brifPrize is properly formatted
        brifPrize: values.brifPrize || []
      };

      if (editingProduct) {
        await updateMutation.mutateAsync({ 
          id: editingProduct.id || editingProduct._id, 
          data: processedValues 
        });
      } else {
        await createMutation.mutateAsync(processedValues);
      }
      
      onClose();
      form.resetFields();
      setMainImage('');
      setAdditionalImages([]);
    } catch (error) {
      // Error handled by mutation
    } finally {
      setLoading(false);
    }
  };

  const handleMainImageChange = (e) => {
    setMainImage(e.target.value);
  };

  const handleAddImage = () => {
    setAdditionalImages([...additionalImages, '']);
  };

  const handleImageChange = (index, value) => {
    const newImages = [...additionalImages];
    newImages[index] = value;
    setAdditionalImages(newImages);
  };

  const handleRemoveImage = (index) => {
    const newImages = additionalImages.filter((_, i) => i !== index);
    setAdditionalImages(newImages);
  };

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          {editingProduct ? (
            <>
              <EditOutlined className="text-blue-500" />
              <span>Edit Product</span>
            </>
          ) : (
            <>
              <PlusOutlined className="text-green-500" />
              <span>Add New Product</span>
            </>
          )}
        </div>
      }
      open={open}
      onCancel={onClose}
      width={800}
      style={{ top: 20 }}
      footer={null}
      className="product-form-modal"
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          type: 'single',
          inStock: true,
          allowCustomMessage: false,
          messageOnCard: true,
          brifPrize: [],
          tags: ''
        }}
      >
        <div className="max-h-[70vh] overflow-y-auto pr-2">
          <Row gutter={16}>
            {/* Left Column */}
            <Col span={12}>
              <Form.Item
                name="name"
                label="Product Name"
                rules={[
                  { required: true, message: 'Please enter product name' },
                  { min: 2, message: 'Minimum 2 characters' }
                ]}
              >
                <Input placeholder="e.g., Red Rose Bouquet" />
              </Form.Item>

              <Form.Item
                name="type"
                label="Type"
                rules={[{ required: true, message: 'Please select type' }]}
              >
                <Select placeholder="Select type">
                  <Option value="single">Single</Option>
                
                </Select>
              </Form.Item>

              <div className="grid grid-cols-2 gap-4">
                <Form.Item
                  name="price"
                  label="Price (₹)"
                  rules={[
                    { required: true, message: 'Please enter price' },
                    { type: 'number', min: 1, message: 'Minimum ₹1' }
                  ]}
                >
                  <InputNumber 
                    placeholder="499" 
                    className="w-full"
                    min={1}
                  />
                </Form.Item>

                <Form.Item
                  name="originalPrice"
                  label="Original Price (₹)"
                  rules={[
                    { required: true, message: 'Please enter original price' },
                    { type: 'number', min: 1, message: 'Minimum ₹1' }
                  ]}
                >
                  <InputNumber 
                    placeholder="999" 
                    className="w-full"
                    min={1}
                  />
                </Form.Item>
              </div>

              <Form.Item
                name="category"
                label="Category"
                rules={[{ required: true, message: 'Please select category' }]}
                tooltip="Category name as string (not ID)"
              >
                <Select 
                  placeholder="Select category" 
                  showSearch
                  filterOption={(input, option) =>
                    (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {categories.map(cat => (
                    <Option key={cat.slug} value={cat.slug}>
                      {cat.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item label="Main Image URL">
                <Input
                  value={mainImage}
                  onChange={handleMainImageChange}
                  placeholder="https://example.com/image.jpg"
                />
                {mainImage && (
                  <div className="mt-2">
                    <Image
                      src={mainImage}
                      alt="Preview"
                      width={100}
                      height={100}
                      className="rounded border"
                      fallback="https://via.placeholder.com/100"
                    />
                  </div>
                )}
              </Form.Item>
            </Col>

            {/* Right Column */}
            <Col span={12}>
              <Form.Item
                name="description"
                label="Description"
                rules={[{ required: true, message: 'Please enter description' }]}
              >
                <TextArea 
                  rows={4} 
                  placeholder="Enter product description" 
                />
              </Form.Item>

              <div className="grid grid-cols-2 gap-4">
                <Form.Item
                  name="inStock"
                  label="In Stock"
                  valuePropName="checked"
                >
                  <Switch checkedChildren="Yes" unCheckedChildren="No" />
                </Form.Item>

                <Form.Item
                  name="allowCustomMessage"
                  label="Allow Custom Message"
                  valuePropName="checked"
                >
                  <Switch checkedChildren="Yes" unCheckedChildren="No" />
                </Form.Item>

                <Form.Item
                  name="messageOnCard"
                  label="Message on Card"
                  valuePropName="checked"
                >
                  <Switch checkedChildren="Yes" unCheckedChildren="No" />
                </Form.Item>
              </div>

              <Form.Item
                name="tags"
                label="Tags"
                tooltip="Comma separated tags (e.g., roses,popular,valentine)"
              >
                <Input placeholder="roses,popular,valentine" />
              </Form.Item>

              <Form.Item label="Additional Images">
                <div className="space-y-2">
                  {additionalImages.map((img, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Input
                        value={img}
                        onChange={(e) => handleImageChange(index, e.target.value)}
                        placeholder="https://example.com/image2.jpg"
                        className="flex-1"
                      />
                      <Button
                        danger
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemoveImage(index)}
                      />
                    </div>
                  ))}
                  <Button type="dashed" onClick={handleAddImage} block>
                    Add Image URL
                  </Button>
                </div>
              </Form.Item>
            </Col>
          </Row>

          {/* Brief Price Array - Full Width */}
          <Divider orientation="left">
            <InfoCircleOutlined /> Brief Price Options
          </Divider>
          <Form.Item
            name="brifPrize"
            label="Price Variations"
            tooltip="Add different price options like [{ 'Make Heart Shape': 14 }]"
          >
            <BriefPriceArray />
          </Form.Item>

          {/* Form Actions */}
          <Form.Item className="mb-0 mt-6">
            <div className="flex justify-end gap-3">
              <Button onClick={onClose} size="large">
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                size="large"
                loading={loading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {editingProduct ? 'Update Product' : 'Create Product'}
              </Button>
            </div>
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
};

// ====================
// 6. MAIN COMPONENT
// ====================
const ProductManagement = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  // TanStack Query hooks
  const { data: categories = [], isLoading: catsLoading } = useCategories();
  const { data: products = [], isLoading: prodsLoading, error } = useProducts();
  const deleteMutation = useDeleteProduct();

  const isLoading = catsLoading || prodsLoading;

  // Filter products
  const filteredProducts = products.filter(product => {
    const matchesSearch = 
      product.name?.toLowerCase().includes(searchText.toLowerCase()) ||
      product.description?.toLowerCase().includes(searchText.toLowerCase()) ||
      product.tags?.some(tag => tag.toLowerCase().includes(searchText.toLowerCase()));
    
    const matchesCategory = 
      selectedCategory === 'all' || 
      product.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  // Handle edit
  const handleEdit = (product) => {
    setEditingProduct({
      ...product,
      // Ensure category is string name
      category: product.category
    });
    setModalOpen(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    await deleteMutation.mutateAsync(id);
  };

  // Reset form
  const handleModalClose = () => {
    setModalOpen(false);
    setEditingProduct(null);
  };

  // Product detail view
  const ExpandedRow = ({ product }) => (
    <div className="p-4 bg-gray-50 rounded">
      <Row gutter={16}>
        <Col span={8}>
          <div className="space-y-2">
            <div>
              <strong>Category:</strong> 
              <Tag color="blue" className="ml-2">{product.category}</Tag>
            </div>
            <div>
              <strong>Type:</strong> 
              <Tag className="ml-2">{product.type}</Tag>
            </div>
            <div>
              <strong>Stock:</strong> 
              <Tag color={product.inStock ? "green" : "red"} className="ml-2">
                {product.inStock ? "In Stock" : "Out of Stock"}
              </Tag>
            </div>
            <div>
              <strong>Custom Message:</strong> 
              <Tag color={product.allowCustomMessage ? "green" : "red"} className="ml-2">
                {product.allowCustomMessage ? "Allowed" : "Not Allowed"}
              </Tag>
            </div>
          </div>
        </Col>
        <Col span={8}>
          <div className="space-y-2">
            <div>
              <strong>Price:</strong> 
              <span className="ml-2 text-lg font-bold text-green-600">₹{product.price}</span>
            </div>
            <div>
              <strong>Original Price:</strong> 
              <span className="ml-2 text-lg font-bold text-red-500 line-through">₹{product.originalPrice}</span>
            </div>
            <div>
              <strong>Discount:</strong> 
              <span className="ml-2 font-bold text-orange-600">
                {Math.round((1 - product.price/product.originalPrice) * 100)}% OFF
              </span>
            </div>
          </div>
        </Col>
        <Col span={8}>
          <div>
            <strong>Tags:</strong>
            <div className="mt-2">
              {product.tags?.map(tag => (
                <Tag key={tag} color="purple" className="m-1">{tag}</Tag>
              ))}
            </div>
          </div>
        </Col>
      </Row>
      
      {product.brifPrize && product.brifPrize.length > 0 && (
        <div className="mt-4">
          <strong>Price Options:</strong>
          <div className="mt-2 space-y-1">
            {product.brifPrize.map((item, idx) => {
              const [key, value] = Object.entries(item)[0];
              return (
                <div key={idx} className="flex justify-between bg-white p-2 rounded border">
                  <span>{key}</span>
                  <span className="font-bold">₹{value}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  // Table columns
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: (a, b) => a.id - b.id,
      render: (id) => <Tag color="geekblue">#{id}</Tag>,
    },
    {
      title: 'Product',
      key: 'product',
      render: (_, record) => (
        <div className="flex items-center gap-3">
          <Image
            src={record.image}
            alt={record.name}
            width={50}
            height={50}
            className="rounded border object-cover"
            fallback="https://via.placeholder.com/50"
          />
          <div>
            <div className="font-medium">{record.name}</div>
            <div className="text-xs text-gray-500 truncate max-w-xs">
              {record.description}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (category) => (
        <Tag color="blue">{category}</Tag>
      ),
      filters: categories.map(cat => ({ text: cat.name, value: cat.slug })),
      onFilter: (value, record) => record.category === value,
    },
    {
      title: 'Price',
      key: 'price',
      render: (_, record) => (
        <div>
          <div className="font-bold text-green-600">₹{record.price}</div>
          <div className="text-xs text-gray-500 line-through">₹{record.originalPrice}</div>
        </div>
      ),
      sorter: (a, b) => a.price - b.price,
    },
    {
      title: 'Stock',
      dataIndex: 'inStock',
      key: 'inStock',
      render: (inStock) => (
        <Tag color={inStock ? "green" : "red"}>
          {inStock ? "In Stock" : "Out"}
        </Tag>
      ),
      filters: [
        { text: 'In Stock', value: true },
        { text: 'Out of Stock', value: false },
      ],
      onFilter: (value, record) => record.inStock === value,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
            className="bg-blue-500 hover:bg-blue-600"
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete this product?"
            description="Are you sure you want to delete this product?"
            onConfirm={() => handleDelete(record.id || record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingOutlined className="text-4xl text-blue-500 mb-4" spin />
          <p className="text-gray-600">Loading products...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-lg shadow-md">
          <div className="text-red-500 text-4xl mb-4">⚠️</div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Error Loading Products</h3>
          <p className="text-gray-600 mb-4">Failed to fetch products from server</p>
          <Button 
            type="primary" 
            onClick={() => window.location.reload()}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold">P</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800">Product Management</h1>
          </div>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Manage products with all fields exactly as specified. Category stored as string name.
          </p>
        </div>

        {/* Stats */}
        <Row gutter={[16, 16]} className="mb-6">
          <Col xs={24} sm={12} md={6}>
            <Card className="text-center shadow-md hover:shadow-lg transition-all">
              <div className="text-2xl font-bold text-blue-600">{products.length}</div>
              <div className="text-gray-600">Total Products</div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card className="text-center shadow-md hover:shadow-lg transition-all">
              <div className="text-2xl font-bold text-green-600">
                {products.filter(p => p.inStock).length}
              </div>
              <div className="text-gray-600">In Stock</div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card className="text-center shadow-md hover:shadow-lg transition-all">
              <div className="text-2xl font-bold text-purple-600">
                {new Set(products.map(p => p.category)).size}
              </div>
              <div className="text-gray-600">Categories</div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card className="text-center shadow-md hover:shadow-lg transition-all">
              <div className="text-2xl font-bold text-orange-600">
                {filteredProducts.length}
              </div>
              <div className="text-gray-600">Filtered</div>
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Card className="mb-6 shadow-sm">
          <Row gutter={16} align="middle">
            <Col xs={24} md={12}>
              <Input
                placeholder="Search products by name, description or tags..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                size="large"
                className="w-full"
                allowClear
              />
            </Col>
            <Col xs={12} md={6}>
              <Select
                value={selectedCategory}
                onChange={setSelectedCategory}
                size="large"
                className="w-full"
              >
                <Option value="all">All Categories</Option>
                {categories.map(cat => (
                  <Option key={cat.slug} value={cat.slug}>
                    {cat.name}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={12} md={6}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditingProduct(null);
                  setModalOpen(true);
                }}
                size="large"
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              >
                Add Product
              </Button>
            </Col>
          </Row>
        </Card>

        {/* Products Table */}
        <Card className="shadow-xl rounded-xl overflow-hidden border-0">
          <div className="p-4 border-b bg-gray-50">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-gray-800">All Products</h2>
                <p className="text-gray-500 text-sm">
                  Showing {filteredProducts.length} of {products.length} products
                </p>
              </div>
              <Button 
                type="text" 
                onClick={() => {
                  setSearchText('');
                  setSelectedCategory('all');
                }}
              >
                Clear Filters
              </Button>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <Table
              columns={columns}
              dataSource={filteredProducts}
              rowKey={record => record.id || record._id}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `${total} products`,
                className: 'px-4'
              }}
              expandable={{
                expandedRowRender: (record) => <ExpandedRow product={record} />,
                rowExpandable: (record) => true,
              }}
              className="product-table"
              size="middle"
              scroll={{ x: true }}
            />
          </div>
        </Card>

        {/* Form Modal */}
        <ProductForm
          open={modalOpen}
          onClose={handleModalClose}
          editingProduct={editingProduct}
          categories={categories}
        />
      </div>
    </div>
  );
};

 
export default ProductManagement;